import { useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  HStack,
  Text,
  VStack,
} from '@eluve/components';

import {
  getLlmModelArgsForOverrideQuery,
  getPromptTemplatesForOverrideQuery,
} from '../operations';

import { OverrideSection } from './OverrideSection';
import { STT_PROVIDER_OPTIONS, SttProviderValue, VariantConfig } from './types';

interface SttProviderOverrideProps {
  config: VariantConfig;
  onChange: (updates: Partial<VariantConfig>) => void;
}

export const SttProviderOverride: React.FC<SttProviderOverrideProps> = ({
  config,
  onChange,
}) => {
  const { data: promptTemplatesData } = useQuery(
    getPromptTemplatesForOverrideQuery,
  );
  const { data: modelArgsData } = useQuery(getLlmModelArgsForOverrideQuery);

  const hasValues =
    config.STT_PROVIDER !== undefined ||
    config.GEMINI_PROMPT !== undefined ||
    config.GEMINI_MODEL_ARG !== undefined;

  const [isExpanded, setIsExpanded] = useState(hasValues);

  const isGemini = config.STT_PROVIDER === 'g';

  const transcriptionPrompts = useMemo(() => {
    return (
      promptTemplatesData?.promptTemplates.filter(
        (p) => p.outputType === 'TRANSCRIPTION',
      ) ?? []
    );
  }, [promptTemplatesData]);

  const geminiModelArgs = useMemo(() => {
    return (
      modelArgsData?.llmModelArgs.filter((m) => m.modelType === 'GEMINI') ?? []
    );
  }, [modelArgsData]);

  const handleEnabledChange = (enabled: boolean) => {
    setIsExpanded(enabled);
    if (!enabled) {
      onChange({
        STT_PROVIDER: undefined,
        GEMINI_PROMPT: undefined,
        GEMINI_MODEL_ARG: undefined,
      });
    }
  };

  const handleProviderChange = (value: SttProviderValue) => {
    if (value !== 'g') {
      onChange({
        STT_PROVIDER: value,
        GEMINI_PROMPT: undefined,
        GEMINI_MODEL_ARG: undefined,
      });
    } else {
      onChange({ STT_PROVIDER: value });
    }
  };

  const selectedProvider = STT_PROVIDER_OPTIONS.find(
    (o) => o.value === config.STT_PROVIDER,
  );

  const selectedPrompt = transcriptionPrompts.find(
    (p) => p.id === config.GEMINI_PROMPT,
  );

  const selectedModelArg = geminiModelArgs.find(
    (m) => m.id === config.GEMINI_MODEL_ARG,
  );

  return (
    <OverrideSection
      label="Override STT Provider"
      enabled={isExpanded}
      onEnabledChange={handleEnabledChange}
    >
      <VStack gap={3}>
        <VStack gap={1}>
          <Text size="sm" color="secondary">
            Provider
          </Text>
          <Combobox>
            <ComboboxSelectButton className="w-full">
              {selectedProvider?.label ?? 'Select provider'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search providers...">
              <ComboboxEmpty>No providers found</ComboboxEmpty>
              {STT_PROVIDER_OPTIONS.map((option) => (
                <ComboboxOption
                  key={option.value}
                  onSelect={() => handleProviderChange(option.value)}
                  searchValue={option.label}
                >
                  {option.label}
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </VStack>

        {isGemini && (
          <>
            <VStack gap={1}>
              <Text size="sm" color="secondary">
                Transcription Prompt
              </Text>
              <Combobox>
                <ComboboxSelectButton className="w-full">
                  {selectedPrompt?.name ?? 'Select prompt (optional)'}
                </ComboboxSelectButton>
                <ComboboxDropdown searchPlaceholder="Search prompts...">
                  <ComboboxEmpty>No prompts found</ComboboxEmpty>
                  {transcriptionPrompts.map((prompt) => (
                    <ComboboxOption
                      key={prompt.id}
                      onSelect={() => onChange({ GEMINI_PROMPT: prompt.id })}
                      searchValue={`${prompt.name} ${prompt.id}`}
                    >
                      <HStack justify="between" gap={2}>
                        <Text>{prompt.name}</Text>
                        <Text size="sm" className="font-mono" color="tertiary">
                          {prompt.id.slice(0, 8)}
                        </Text>
                      </HStack>
                    </ComboboxOption>
                  ))}
                </ComboboxDropdown>
              </Combobox>
            </VStack>

            <VStack gap={1}>
              <Text size="sm" color="secondary">
                Gemini Model Args
              </Text>
              <Combobox>
                <ComboboxSelectButton className="w-full">
                  {selectedModelArg
                    ? selectedModelArg.name ?? selectedModelArg.modelType
                    : 'Select model args (optional)'}
                </ComboboxSelectButton>
                <ComboboxDropdown searchPlaceholder="Search models...">
                  <ComboboxEmpty>No models found</ComboboxEmpty>
                  {geminiModelArgs.map((arg) => (
                    <ComboboxOption
                      key={arg.id}
                      onSelect={() => onChange({ GEMINI_MODEL_ARG: arg.id })}
                      searchValue={`${arg.name ?? ''} ${arg.modelType} ${arg.id}`}
                    >
                      <HStack justify="between" gap={2}>
                        <Text>{arg.name ?? arg.modelType}</Text>
                        <Text size="sm" className="font-mono" color="tertiary">
                          {arg.id.slice(0, 8)}
                        </Text>
                      </HStack>
                    </ComboboxOption>
                  ))}
                </ComboboxDropdown>
              </Combobox>
            </VStack>
          </>
        )}
      </VStack>
    </OverrideSection>
  );
};
